import type {
  SearchPageActiveFilter,
  SearchPageFilterDefinition,
  SearchPageFilterState,
} from '../sharedTypes';

const getUniqueValues = (values: string[]) => Array.from(new Set(values));

export const sanitizeFilterValues = (definition: SearchPageFilterDefinition, values: string[]) => {
  const selectedValueSet = new Set(getUniqueValues(values));

  return definition.options
    .filter((option) => selectedValueSet.has(option.value))
    .map((option) => option.value);
};

export const createEmptyFilterSelections = <FilterName extends string>(filterNames: FilterName[]) =>
  filterNames.reduce<Record<FilterName, string[]>>(
    (selectedFilters, filterName) => {
      selectedFilters[filterName] = [];
      return selectedFilters;
    },
    {} as Record<FilterName, string[]>
  );

export const createDefaultFilterSelections = <FilterName extends string>(
  filterDefinitions: Record<FilterName, SearchPageFilterDefinition>,
  filterNames: FilterName[]
) =>
  filterNames.reduce<Record<FilterName, string[]>>(
    (selectedFilters, filterName) => {
      selectedFilters[filterName] = sanitizeFilterValues(
        filterDefinitions[filterName],
        filterDefinitions[filterName].defaultSelectedValues ?? []
      );
      return selectedFilters;
    },
    {} as Record<FilterName, string[]>
  );

type CreateFilterStatesArgs<FilterName extends string> = {
  filterDefinitions: Record<FilterName, SearchPageFilterDefinition>;
  filterNames: FilterName[];
  selectedFilters: Record<FilterName, string[]>;
  setFilterValues: (filterName: FilterName, values: string[]) => void;
  toggleFilterValue: (filterName: FilterName, value: string) => void;
  clearFilter: (filterName: FilterName) => void;
};

export const createFilterStates = <FilterName extends string>(
  args: CreateFilterStatesArgs<FilterName>
) => {
  const {
    filterDefinitions,
    filterNames,
    selectedFilters,
    setFilterValues,
    toggleFilterValue,
    clearFilter,
  } = args;

  return filterNames.reduce<Record<FilterName, SearchPageFilterState>>(
    (filterState, filterName) => {
      const definition = filterDefinitions[filterName];
      const values = selectedFilters[filterName];

      filterState[filterName] = {
        label: definition.label,
        options: definition.options,
        selectedValues: values,
        setSelectedValues: (nextValues) => setFilterValues(filterName, nextValues),
        toggleValue: (value) => toggleFilterValue(filterName, value),
        clear: () => clearFilter(filterName),
        isSelected: (value) => values.includes(value),
      };

      return filterState;
    },
    {} as Record<FilterName, SearchPageFilterState>
  );
};

type CreateActiveFiltersArgs<FilterName extends string> = {
  filterDefinitions: Record<FilterName, SearchPageFilterDefinition>;
  filterNames: FilterName[];
  selectedFilters: Record<FilterName, string[]>;
  appliedQuery: string;
  clearQuery: () => void;
  toggleFilterValue: (filterName: FilterName, value: string) => void;
};

export const createActiveFilters = <FilterName extends string>(
  args: CreateActiveFiltersArgs<FilterName>
) => {
  const {
    filterDefinitions,
    filterNames,
    selectedFilters,
    appliedQuery,
    clearQuery,
    toggleFilterValue,
  } = args;

  return filterNames.reduce<SearchPageActiveFilter<FilterName>[]>(
    (currentActiveFilters, filterName) => {
      const definition = filterDefinitions[filterName];

      selectedFilters[filterName].forEach((value) => {
        const option = definition.options.find((item) => item.value === value);

        currentActiveFilters.push({
          type: 'filter',
          filterName,
          filterLabel: definition.label,
          label: option?.label ?? value,
          value,
          clear: () => toggleFilterValue(filterName, value),
        });
      });

      return currentActiveFilters;
    },
    appliedQuery
      ? [
          {
            type: 'query',
            label: appliedQuery,
            value: appliedQuery,
            clear: clearQuery,
          },
        ]
      : []
  );
};

type CreateSearchCriteriaViewModelArgs<FilterName extends string> =
  CreateFilterStatesArgs<FilterName> & CreateActiveFiltersArgs<FilterName>;

export const createSearchCriteriaViewModel = <FilterName extends string>(
  args: CreateSearchCriteriaViewModelArgs<FilterName>
) => {
  const filters = createFilterStates(args);
  const activeFilters = createActiveFilters(args);

  return {
    filters,
    activeFilters,
    hasActiveFilters: activeFilters.length > 0,
  };
};
